import {inject, Injectable, OnDestroy, Signal, signal} from '@angular/core';
import {TipoMensajesService} from "./tipo-mensajes.service";
import {IndexedDbService} from "./indexed-db-service.service";
import {EnumTablasIndexedDB} from "../models/enum/enum-indexed-db";
import {ClsUtil} from "../../util/cls-util";
import {AppInfoService} from "./app-info.service";


@Injectable({
  providedIn: 'root'
})
export class ConexionRedService implements OnDestroy {
  private tipoMsg = inject(TipoMensajesService);
  private indexedDb = inject(IndexedDbService);
  public infApp = inject(AppInfoService);

  private onlineSignal = signal<boolean>(navigator.onLine);
  public isOnline: Signal<boolean> = this.onlineSignal;

  public lastOnlineAt = signal<Date | null>(navigator.onLine ? new Date() : null);
  public lastOfflineAt = signal<Date | null>(!navigator.onLine ? new Date() : null);

  /** Señal de red lenta */
  private slowNetworkSignal = signal<boolean>(false);
  public isSlowNetwork: Signal<boolean> = this.slowNetworkSignal;

  private lastMbps = signal<number | null>(null);
  private lastRtt = signal<number | null>(null);
  public currentMbps: Signal<number | null> = this.lastMbps;
  public currentRtt: Signal<number | null> = this.lastRtt;

  // Puedes ajustar el tamaño del archivo con el parámetro bytes (ejemplo: 1000000 = 1 MB, 5000000 = 5 MB, 10000000 = 10 MB).
  private testFileUrl = 'https://speed.cloudflare.com/__down?bytes=3000000'; // Archivo pequeño para test
  private testInterval = 60 * 1000; // Cada 60s
  private intervalId?: any;

  constructor() {
    window.addEventListener('online', async () => {
      this.onlineSignal.set(true)
      this.lastOnlineAt.set(new Date());
      await this.checkNetworkSpeed();
    });

    window.addEventListener('offline', () => {
      this.onlineSignal.set(false)
      this.lastOfflineAt.set(new Date());
      this.slowNetworkSignal.set(true);
    });
  }

  public async startSpeedCheck() {
    // await this.indexedDb.resetDB();
    await this.checkNetworkSpeed();
    this.intervalId = setInterval(() => this.checkNetworkSpeed(), this.testInterval);
  }

  public async checkNetworkSpeed() {
    await this.esLentaLaRed();
    if (this.slowNetworkSignal() && !this.onlineSignal()) {
      let mensaje: string = ClsUtil.getTranslations(['MENSAJES.TOAS.RED_LENTO_LATENCIA'], this.infApp)['MENSAJES.TOAS.RED_LENTO_LATENCIA'].toString();
      mensaje = mensaje.replace('{0}', this.lastMbps().toFixed(2)).replace('{1}', this.lastRtt().toFixed(0));
      this.tipoMsg.mostrarToast(mensaje, 5000);
      await this.indexedDb.guardarDato(EnumTablasIndexedDB.REDLENTA, {
        nombre: this.infApp.usuarioLogeado.email,
        infoRed: mensaje,
        fecha: ClsUtil.formatoFechaManualPipe(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        estado: 'red lenta'
      });
    }
  }

  public async esLentaLaRed() {
    try {
      if (!navigator.onLine) {
        this.slowNetworkSignal.set(true);
        this.lastMbps.set(null);
        this.lastRtt.set(null);
        return;
      }

      // const {mbps, rtt} = await this.testSpeed();
      const { mbps, rtt } = await this.testSpeedParallel(1); // 4 descargas en paralelo
      console.log('mbps', mbps);
      console.log('rtt', rtt);
      this.lastMbps.set(mbps);
      this.lastRtt.set(rtt);
      const lento = mbps < 5;
      console.log('lento', lento);
      this.slowNetworkSignal.set(lento);
    } catch (e) {
      console.error('Error midiendo velocidad de red', e.message);
      this.slowNetworkSignal.set(true);
      this.lastMbps.set(null);
      this.lastRtt.set(null);
    }
  }

  private async testSpeedParallel(parallel = 4): Promise<{ mbps: number; rtt: number }> {
    if (!navigator.onLine) return { mbps: 0, rtt: 0 };

    const start = performance.now();

    // Creamos N promesas de descarga en paralelo
    const downloads = Array.from({ length: parallel }, async () => {
      const response = await fetch(this.testFileUrl, { cache: 'no-cache' });
      const blob = await response.blob();
      return blob.size;
    });

    const sizes = await Promise.all(downloads);
    const end = performance.now();

    const totalBytes = sizes.reduce((acc, val) => acc + val, 0);
    const seconds = (end - start) / 1000;
    const mbps = (totalBytes * 8) / (seconds * 1024 * 1024);

    const rtt = (end - start) / 2; // RTT aproximada

    return { mbps, rtt };
  }

  public resetSpeedCheck(): void {
    clearInterval(this.intervalId);
  }

  ngOnDestroy() {
    this.resetSpeedCheck();
  }
}
